<template>
  <transition name="qti-fade">
    <div v-if="open" class="qti-overlay" role="dialog" aria-modal="true" aria-labelledby="qti-title" @click.self="close">
      <div class="qti-dialog">
        <header class="qti-header">
          <div>
            <div id="qti-title" class="qti-title">Import QTI</div>
            <div class="qti-subtitle">
              Drop a QTI 2.x XML file (single item or test wrapper). Single &amp; multiple-choice
              <code>choiceInteraction</code> items are supported — others will be reported and skipped.
            </div>
          </div>
          <button class="qti-close" title="Close" @click="close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </header>

        <div class="qti-body">
          <!-- Step 1: file picker (visible until a file is parsed) -->
          <div v-if="!parsed.length && !parseError"
               class="qti-drop"
               :class="{ dragging }"
               @dragover.prevent="dragging=true"
               @dragleave="dragging=false"
               @drop.prevent="onDrop">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="color:var(--accent)"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <div class="qti-drop-title">Drop QTI XML here</div>
            <div class="qti-drop-or">or</div>
            <label class="qti-drop-btn">
              <input type="file" accept=".xml,application/xml,text/xml" @change="onFile" hidden />
              Choose a file
            </label>
            <div class="qti-drop-hint">Accepted: <code>.xml</code> · QTI 2.x</div>
          </div>

          <!-- Parse error -->
          <div v-if="parseError" class="qti-error">
            <div class="qti-error-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              Could not parse QTI file
            </div>
            <div class="qti-error-body">{{ parseError }}</div>
            <button class="btn-ghost" @click="reset">Try another file</button>
          </div>

          <!-- Parsed items list -->
          <div v-if="parsed.length" class="qti-result">
            <div class="qti-result-head">
              <div class="qti-result-summary">
                <b>{{ parsed.length }}</b> item{{ parsed.length === 1 ? '' : 's' }} detected ·
                <b style="color:var(--success)">{{ supportedCount }}</b> importable ·
                <span v-if="unsupportedCount > 0" style="color:var(--muted)"><b>{{ unsupportedCount }}</b> skipped</span>
              </div>
              <div class="qti-result-actions">
                <button class="qti-mini-btn" @click="selectAllSupported">Select all</button>
                <button class="qti-mini-btn" @click="selectNone">Select none</button>
                <button class="qti-mini-btn danger" @click="reset">Discard</button>
              </div>
            </div>

            <div class="qti-items">
              <label v-for="(item, i) in parsed" :key="i" class="qti-item" :class="{ unsupported: !item.ok, selected: item.ok && selected[i] }">
                <input v-if="item.ok"
                       type="checkbox"
                       :checked="selected[i]"
                       @change="toggleSelection(i)" />
                <span v-else class="qti-item-skip">SKIP</span>
                <div class="qti-item-body">
                  <div class="qti-item-title">
                    <span v-if="item.ok" class="qti-item-type" :class="item.question.type === 'multiple_choice' ? 'multi' : 'single'">
                      {{ item.question.type === 'multiple_choice' ? 'Multi' : 'Single' }}
                    </span>
                    <span class="qti-item-id" v-if="item.id">{{ item.id }}</span>
                    <span class="qti-item-name">{{ item.title || (item.ok ? item.question.prompt : '—') }}</span>
                  </div>
                  <div v-if="item.ok" class="qti-item-meta">
                    {{ item.question.options.length }} options ·
                    {{ item.question.options.filter(o => o.correct).length }} correct
                  </div>
                  <div v-else class="qti-item-reason">{{ item.reason }}</div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <footer class="qti-footer">
          <span class="qti-footer-note">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            Imports are appended to the current draft. Nothing is overwritten.
          </span>
          <div class="qti-footer-actions">
            <button class="btn-ghost" @click="close">Cancel</button>
            <button class="btn-primary"
                    :disabled="selectedCount === 0"
                    @click="importSelected">
              Import {{ selectedCount > 0 ? `(${selectedCount})` : '' }}
            </button>
          </div>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { parseQti } from '../data/qtiParser.js';

export default {
  name: 'QtiImportModal',
  data() {
    return {
      dragging: false,
      parsed: [],
      parseError: '',
      selected: {},
      fileName: '',
    };
  },
  computed: {
    open() { return this.$store.state.ui.qtiImportOpen; },
    supportedCount() { return this.parsed.filter(p => p.ok).length; },
    unsupportedCount() { return this.parsed.filter(p => !p.ok).length; },
    selectedCount() { return Object.values(this.selected).filter(Boolean).length; },
  },
  watch: {
    open(val) {
      if (val) {
        this.reset();
        document.addEventListener('keydown', this.onKey);
      } else {
        document.removeEventListener('keydown', this.onKey);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKey);
  },
  methods: {
    onKey(e) { if (e.key === 'Escape') this.close(); },
    close() { this.$store.commit('CLOSE_QTI_IMPORT'); },
    reset() {
      this.parsed = [];
      this.parseError = '';
      this.selected = {};
      this.fileName = '';
      this.dragging = false;
    },
    onFile(e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.readFile(file);
    },
    onDrop(e) {
      this.dragging = false;
      const file = e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.readFile(file);
    },
    readFile(file) {
      this.fileName = file.name;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const items = parseQti(String(reader.result || ''));
          this.parsed = items;
          this.parseError = '';
          // Default: select all supported items.
          const sel = {};
          items.forEach((it, i) => { if (it.ok) sel[i] = true; });
          this.selected = sel;
          if (this.supportedCount === 0) {
            this.parseError = 'The file was parsed, but no single/multiple-choice items were found.';
          }
        } catch (err) {
          this.parseError = err.message || String(err);
          this.parsed = [];
        }
      };
      reader.onerror = () => {
        this.parseError = 'Could not read file (' + (reader.error && reader.error.message) + ')';
      };
      reader.readAsText(file);
    },
    toggleSelection(i) {
      this.$set(this.selected, i, !this.selected[i]);
    },
    selectAllSupported() {
      const sel = {};
      this.parsed.forEach((it, i) => { if (it.ok) sel[i] = true; });
      this.selected = sel;
    },
    selectNone() { this.selected = {}; },
    importSelected() {
      const items = this.parsed
        .map((p, i) => (p.ok && this.selected[i] ? p.question : null))
        .filter(Boolean);
      if (items.length === 0) return;
      this.$store.commit('IMPORT_QUESTIONS', items);
      this.$store.dispatch('toast', `Imported ${items.length} question${items.length === 1 ? '' : 's'} from ${this.fileName}`);
      this.close();
    },
  },
};
</script>

<style scoped>
.qti-overlay {
  position: fixed; inset: 0;
  background: rgba(13, 31, 60, 0.55);
  backdrop-filter: blur(2px);
  display: grid; place-items: center;
  z-index: 600; padding: 24px;
}
.qti-dialog {
  background: var(--card);
  border-radius: 14px;
  width: 100%; max-width: 760px;
  max-height: 92vh;
  overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(0,0,0,0.30);
  border: 1px solid var(--border);
}
.qti-header {
  display: flex; gap: 16px; align-items: flex-start;
  padding: 20px 24px 14px;
  border-bottom: 1px solid var(--border-soft);
}
.qti-title { font-size: 17px; font-weight: 700; color: var(--ink); margin-bottom: 4px; letter-spacing: -0.01em; }
.qti-subtitle { font-size: 12.5px; color: var(--muted); line-height: 1.5; max-width: 580px; }
.qti-subtitle code { background: var(--bg); padding: 1px 5px; border-radius: 4px; font-size: 11.5px; }
.qti-close {
  margin-left: auto;
  background: none; border: none; padding: 4px;
  border-radius: 6px; color: var(--muted); cursor: pointer; flex-shrink: 0;
}
.qti-close:hover { background: var(--bg); color: var(--ink); }

.qti-body {
  padding: 18px 24px;
  flex: 1;
  overflow: auto;
}

.qti-drop {
  border: 2px dashed var(--border-strong);
  border-radius: 12px;
  padding: 36px 18px;
  text-align: center;
  background: var(--bg-alt);
  transition: all 0.15s;
}
.qti-drop.dragging { border-color: var(--accent); background: var(--accent-soft); }
.qti-drop-title { font-size: 15px; font-weight: 700; color: var(--ink); margin-top: 8px; }
.qti-drop-or { font-size: 12px; color: var(--muted); margin: 8px 0; }
.qti-drop-btn {
  display: inline-block;
  padding: 8px 16px;
  background: var(--accent); color: #fff;
  border-radius: 8px;
  font-size: 13px; font-weight: 600;
  cursor: pointer;
  transition: background 0.13s;
}
.qti-drop-btn:hover { background: var(--accent-dark); }
.qti-drop-hint { margin-top: 10px; font-size: 11.5px; color: var(--muted); }
.qti-drop-hint code { background: var(--card); padding: 1px 5px; border-radius: 4px; }

.qti-error {
  border: 1px solid #fecaca;
  background: #fef2f2;
  border-radius: 10px;
  padding: 16px 18px;
  color: #7f1d1d;
}
.qti-error-title {
  display: flex; align-items: center; gap: 8px;
  font-weight: 700; font-size: 14px; margin-bottom: 6px;
}
.qti-error-body { font-size: 13px; line-height: 1.5; margin-bottom: 12px; word-break: break-word; }

.qti-result-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; margin-bottom: 10px; flex-wrap: wrap;
}
.qti-result-summary { font-size: 13px; color: var(--ink-3); }
.qti-result-summary b { color: var(--ink); font-weight: 700; }
.qti-result-actions { display: flex; gap: 6px; }
.qti-mini-btn {
  background: var(--bg);
  border: 1px solid var(--border);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px; font-weight: 500;
  color: var(--ink-3); cursor: pointer;
  transition: all 0.12s;
}
.qti-mini-btn:hover { background: var(--card); color: var(--ink); border-color: var(--accent); }
.qti-mini-btn.danger:hover { color: var(--danger); border-color: var(--danger); }

.qti-items {
  display: flex; flex-direction: column; gap: 6px;
}
.qti-item {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--card);
  cursor: pointer;
  transition: all 0.12s;
}
.qti-item:hover:not(.unsupported) { border-color: var(--accent); }
.qti-item.selected { border-color: var(--accent); background: var(--accent-soft); }
.qti-item.unsupported {
  background: var(--bg-alt); cursor: not-allowed;
  border-style: dashed;
}
.qti-item input[type=checkbox] { margin-top: 2px; flex-shrink: 0; accent-color: var(--accent); }
.qti-item-skip {
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.06em;
  background: var(--muted);
  color: #fff;
  padding: 3px 6px;
  border-radius: 4px;
  flex-shrink: 0;
  margin-top: 1px;
}
.qti-item-body { flex: 1; min-width: 0; }
.qti-item-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.qti-item-type {
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.06em;
  padding: 3px 7px;
  border-radius: 4px;
  text-transform: uppercase;
}
.qti-item-type.single { background: var(--accent-soft); color: var(--accent-dark); }
.qti-item-type.multi  { background: #ede9fe; color: #6d28d9; }
.qti-item-id {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--muted-2);
}
.qti-item-name {
  font-size: 13px; font-weight: 600; color: var(--ink);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  flex: 1; min-width: 0;
}
.qti-item-meta { font-size: 11.5px; color: var(--muted); margin-top: 4px; }
.qti-item-reason { font-size: 11.5px; color: var(--muted); margin-top: 4px; font-style: italic; }

.qti-footer {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
  padding: 14px 24px 18px;
  border-top: 1px solid var(--border-soft);
}
.qti-footer-note {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11.5px; color: var(--muted);
}
.qti-footer-actions { display: flex; gap: 8px; }

.qti-fade-enter-active, .qti-fade-leave-active { transition: opacity 0.18s ease; }
.qti-fade-enter, .qti-fade-leave-to { opacity: 0; }

@media (max-width: 540px) {
  .qti-overlay { padding: 0; align-items: flex-end; }
  .qti-dialog {
    max-width: 100%;
    max-height: 95vh;
    border-radius: 14px 14px 0 0;
    padding-bottom: env(safe-area-inset-bottom);
  }
  .qti-footer-actions { width: 100%; flex-wrap: wrap; }
}
</style>
